import { useState, useEffect } from 'react';
import moment from 'moment';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Loader2, ExternalLink, UserX } from 'lucide-react';
import { userService } from '../../../_services';

function DeactivateReferralLinksDialog({ open, onOpenChange, userName, userId }) {
    const [linkList, setLinkList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [notFound, setNotFound] = useState('');
    const [processingLinkId, setProcessingLinkId] = useState(null);

    useEffect(() => {
        if (open && userName && userId) {
            fetchUserReferralLinks();
        }
    }, [open, userName, userId]);

    const fetchUserReferralLinks = async () => {
        setLoading(true);
        setNotFound('');
        
        try {
            const apidata = await userService.Get_User_ReffralLinks(userName, userId);
            
            if (apidata && (apidata.length > 0 || apidata.length === 0)) {
                if (apidata.code !== "-1") {
                    setLinkList(apidata);
                }
                if (apidata.length === 0) {
                    setNotFound('No active link found');
                }
            }
        } catch (error) {
            console.error('Error fetching referral links:', error);
            setNotFound('Error loading links');
        } finally {
            setLoading(false);
        }
    };

    const handleToggleLink = async (linkId, userId, isActive) => {
        setProcessingLinkId(linkId);
        
        try {
            const apidata = await userService.Update_User_ReffralLinks(linkId, userId, !isActive);
            
            if (apidata && apidata.length > 0 && apidata[0].code !== "-1") {
                // Refresh the list after a short delay
                setTimeout(() => {
                    fetchUserReferralLinks();
                }, 50);
            }
        } catch (error) {
            console.error('Error updating referral link:', error);
        } finally {
            setProcessingLinkId(null);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-3xl">
                <DialogHeader>
                    <DialogTitle className="text-xl font-semibold text-primary">
                        Disable Referral Links
                    </DialogTitle>
                </DialogHeader>

                <div className="space-y-4 py-4">
                    {loading ? (
                        <div className="flex items-center justify-center py-8">
                            <Loader2 className="h-8 w-8 animate-spin text-accent" />
                        </div>
                    ) : notFound ? (
                        <div className="text-center py-8 text-accent">
                            {notFound}
                        </div>
                    ) : (
                        <div className="border rounded-lg overflow-hidden">
                            <Table>
                                <TableHeader>
                                    <TableRow className="bg-gray-50">
                                        <TableHead className="font-semibold">Date Created</TableHead>
                                        <TableHead className="font-semibold">Link</TableHead>
                                        <TableHead className="font-semibold text-center">Actions</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {linkList.length > 0 ? (
                                        linkList.map((link, index) => (
                                            <TableRow key={link.linkId || index}>
                                                <TableCell className="font-medium">
                                                    {moment(link.createdOn).format("YYYY-MM-DD")}
                                                </TableCell>
                                                <TableCell>
                                                    <div className="flex items-center gap-2">
                                                        <span className="text-sm text-muted-foreground truncate max-w-md">
                                                            {link.baseURL}/{link.endPointText}
                                                        </span>
                                                        <a
                                                            href={`${link.baseURL}/${link.endPointText}`}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="text-accent hover:text-primary"
                                                        >
                                                            <ExternalLink className="h-4 w-4" />
                                                        </a>
                                                    </div>
                                                </TableCell>
                                                <TableCell className="text-center">
                                                    {link.isActive ? (
                                                        <TooltipProvider delayDuration={200}>
                                                            <Tooltip>
                                                                <TooltipTrigger asChild>
                                                                    <Button
                                                                        variant="ghost"
                                                                        size="sm"
                                                                        onClick={() => handleToggleLink(link.linkId, link.userId, link.isActive)}
                                                                        disabled={processingLinkId === link.linkId}
                                                                        className="h-8 w-8 p-0"
                                                                    >
                                                                        {processingLinkId === link.linkId ? (
                                                                            <Loader2 className="h-4 w-4 animate-spin" />
                                                                        ) : (
                                                                            <UserX className="h-4 w-4" />
                                                                        )}
                                                                    </Button>
                                                                </TooltipTrigger>
                                                                <TooltipContent>Disable</TooltipContent>
                                                            </Tooltip>
                                                        </TooltipProvider>
                                                    ) : (
                                                        <span className="text-sm text-muted-foreground/70">Disabled</span>
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    ) : (
                                        <TableRow>
                                            <TableCell colSpan={3} className="text-center text-muted-foreground py-8">
                                                No referral links found
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </div>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
}

export default DeactivateReferralLinksDialog;

